import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { employeeRolesManagement } from "../../../../../../hooks/api/EmployeeRolesManagement/EmployeeRolesManagement";
import { getNumberOfUsedFilters } from "../../../../../../services/utils/Filter";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Title } from "../../../../../../themes/Default/components/Title";
import { Title as ListTitle } from "../../../../../../themes/Default/components/List/components/Header/components/Title";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { List } from "../../../../../../themes/Default/components/List";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";

const Roles = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 10;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const navigate = useNavigate();
    const [filters, setFilters] = useState(defaultFilters);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedRole, setSelectedRole] = useState([]);
    const [roles, setRoles] = useState();
    const { useRoleListMutation, useDeleteRoleMutation } = employeeRolesManagement;
    const [roleList, { isLoading }] = useRoleListMutation();
    const [deleteRole] = useDeleteRoleMutation();

    const tabs = [{ title: t("employeeRoles.title"), isActive: true }];

    const gotoNew = () => {
        navigate("/settings/employees/roles/add");
    };

    const gotoEdit = (rolesId) => {
        navigate("/settings/employees/roles/edit/" + rolesId);
    };

    const onPageChange = (currentPage, startPage, limitPage) => {
        setCurrentPage(currentPage);
        setFilters({
            ...filters,
            startPage: startPage,
            limitPage: limitPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedRole((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedRole?.length; i++) {
                            const roleId = selectedRole[i];
                            deleteRole(roleId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    roleList(filters).then((result) => {
                                        const roles = result?.data?.records;
                                        setRoles(roles);
                                        console.log(roles);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(roleId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        if (selectedRole.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedRole]);

    useEffect(() => {
        roleList(filters).then((result) => {
            const roles = result?.data?.records;
            setRoles(roles);
            setTotalRecords(result?.data?.totalRecords);
        });
    }, [filters, roleList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("employeeRoles.addRole")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("employeeRoles.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("employees.title")} icon="fa-gear" />
                    <Link title={t("employeeRoles.title")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader></ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("employeeRoles.name")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("employeeRoles.uniqueKey")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {roles?.length > 0 &&
                                        roles?.map((role, index) => {
                                            return (
                                                <TableRow key={role.id}>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(role.id)}></Button>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={role.id} onChange={() => onSelectionChange(role.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>{role.name}</TableDataCell>
                                                    <TableDataCell>{role.uniqueKey}</TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Roles;
